import { API_BASE_URL } from '../config';
import api from './axiosInstance';

const resolveRegionId = (courtData) => {
  const candidate = courtData?.regionId ?? courtData?.idRegion ?? null;
  if (candidate === '' || candidate === undefined) return null;
  return candidate;
};

/**
 * Obtiene todos los tribunales (sin paginación)
 * @returns {Promise<Array>} - Array de tribunales
 */
export async function getAllCourts() {
  try {
    const response = await api.get(
      `${API_BASE_URL}/ModularTask/GetTribunales`,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    return response.data;
  } catch (error) {
    error.customMessage = 'No se pudo obtener el listado de tribunales.';
    throw error;
  }
}

/**
 * Obtiene tribunales paginados del backend
 * @param {number} page - Número de página (comienza en 1)
 * @param {number} pageSize - Cantidad de registros por página
 * @param {boolean|null} isEnabled - Si true, solo tribunales activos; si false, solo inactivos; si null, todos
 * @param {string} search - Término de búsqueda (opcional)
 * @returns {Promise} - { page, pageSize, total, courts }
 */
export async function getCourtsPaged(
  page = 1,
  pageSize = 10,
  status = 'Activos',
  search = null,
  sortBy = null,
  desc = true
) {
  try {
    const params = {
      page,
      pageSize,
      sortBy,
    };

    // status: 'Todos' | 'Activos' | 'Inactivos' or boolean for isEnabled
    if (typeof status === 'boolean') {
      params.isEnabled = status;
    } else if (status && status !== 'Todos') {
      params.status = status;
    }

    // Solo agregar search si tiene valor
    if (search && search.trim()) {
      params.search = search.trim();
    }

    if (desc) {
      params.desc = desc === 'desc' ? true : false;
    }

    const response = await api.get(`${API_BASE_URL}/JudicialCourts/paged`, {
      headers: {
        'Content-Type': 'application/json',
      },
      params,
    });

    return {
      page: response.data.page,
      pageSize: response.data.pageSize,
      total: response.data.total,
      courts: response.data.data || [],
    };
  } catch (error) {
    console.error('getCourtsPaged failed:', error);
    console.error('Error details:', error.response?.data);
    error.customMessage = 'No se pudo obtener el listado de tribunales.';
    throw error;
  }
}

/**
 * Obtiene un tribunal por ID
 * @param {number} courtId - ID del tribunal
 * @returns {Promise} - El tribunal
 */
export async function getCourtById(courtId) {
  try {
    const response = await api.get(
      `${API_BASE_URL}/JudicialCourts/${courtId}`,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error('getCourtById failed:', error);
    console.error('Error details:', error.response?.data);
    error.customMessage = 'No se pudo obtener el tribunal.';
    throw error;
  }
}

/**
 * Crea un nuevo tribunal
 * @param {object} courtData - Datos del tribunal { name, isEnabled }
 * @returns {Promise} - El tribunal creado
 */
export async function createCourt(courtData) {
  try {
    const payload = {
      name: courtData.name || '',
      isEnabled: courtData.isEnabled ?? true,
    };
    const regionId = resolveRegionId(courtData);
    if (regionId !== null) {
      payload.regionId = regionId;
    }

    const response = await api.post(`${API_BASE_URL}/JudicialCourts`, payload, {
      headers: { 'Content-Type': 'application/json' },
    });

    return response.data;
  } catch (error) {
    console.error('createCourt failed:', error);
    console.error('Error details:', error.response?.data);
    error.customMessage = 'No se pudo crear el tribunal.';
    throw error;
  }
}

/**
 * Actualiza un tribunal existente
 * @param {number} courtId - ID del tribunal
 * @param {object} courtData - Datos actualizados { name, isEnabled }
 * @returns {Promise} - El tribunal actualizado
 */
export async function updateCourt(courtId, courtData) {
  try {
    const payload = {
      name: courtData.name || '',
      isEnabled: courtData.isEnabled ?? true,
    };
    const regionId = resolveRegionId(courtData);
    if (regionId !== null) {
      payload.regionId = regionId;
    }

    const response = await api.put(
      `${API_BASE_URL}/JudicialCourts/${courtId}`,
      payload,
      {
        headers: { 'Content-Type': 'application/json' },
      }
    );

    return response.data;
  } catch (error) {
    console.error('updateCourt failed:', error);
    console.error('Error details:', error.response?.data);
    error.customMessage = 'No se pudo actualizar el tribunal.';
    throw error;
  }
}

/**
 * Elimina un tribunal (borrado lógico)
 * @param {number} courtId - ID del tribunal a eliminar
 * @returns {Promise}
 */
export async function deleteCourt(courtId) {
  try {
    const response = await api.delete(
      `${API_BASE_URL}/JudicialCourts/${courtId}`,
      {
        headers: { 'Content-Type': 'application/json' },
      }
    );

    return response.data;
  } catch (error) {
    console.error('deleteCourt failed:', error);
    console.error('Error details:', error.response?.data);
    error.customMessage = 'No se pudo eliminar el tribunal.';
    throw error;
  }
}
